/**
 * src/gm/employees/api-client.ts — Moonshot API 调用封装（BYOK 浏览器直连，spike 实测零代理可行）。
 * 继承旧版全部已验证事实（设计稿 §9）：
 *  ① 所有 kimi-k3 调用强制带 reasoning_effort（不传会闷头推理 280s 零可见输出）；
 *  ② SSE 同时处理 delta.content 与 delta.reasoning_content 两个通道；
 *  ③ 显式 max_tokens=32768；finish_reason='length' 交由上层续写协议；
 *  ④ 429/5xx 指数退避 3 次（2s/8s/20s）；重试复用完整 message 历史吃 prompt 缓存；
 *  ⑤ usage 精确记账：每次响应的 prompt+completion 累计。
 *
 * 安全红线：Key 只从 keyProvider 取出放进 Authorization 头，永不进日志/报错/事件/会话。
 */

import { API_ENDPOINT, MAX_TOKENS, RETRY_DELAYS_MS } from '../platform/config'
import { GmError, translateHttpError } from '../platform/errors'
import type { ModelChoice } from '../platform/model-map'

export interface ChatMessage {
  role: 'system' | 'user' | 'assistant'
  content: string
}

export interface Usage {
  prompt_tokens: number
  completion_tokens: number
  total_tokens: number
  reasoning_tokens?: number
}

export interface ChatResult {
  content: string
  /** 推理通道全文（k3 思考过程；仅用于 UI "思考中"展示，不进存档） */
  reasoning: string
  finishReason: string | null
  usage: Usage
}

export interface ChatOptions {
  messages: ChatMessage[]
  choice: ModelChoice
  /** OpenAI 风格 response_format（json_schema 结构化输出，spike §4 实测遵从度满分） */
  responseFormat?: Record<string, unknown>
  stream?: boolean
  /** 流式增量回调：channel 区分正文/推理两通道；received 为累计字符数 */
  onToken?: (channel: 'content' | 'reasoning', text: string, received: number) => void
}

export interface ApiClientDeps {
  fetchImpl?: typeof fetch
  sleep?: (ms: number) => Promise<void>
  /** BYOK：由外壳注入（读 thirdplay:v2:apikey）；缺省 = 无 Key → NO_API_KEY */
  keyProvider?: () => string | null
}

const defaultSleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms))

/**
 * 流中断哨兵 finishReason：读流异常时已收到部分正文，不抛错而是带哨兵返回，
 * 交给上层续写协议（与 finish_reason='length' 同路径，≤1 次）。
 */
export const STREAM_INTERRUPTED = 'stream-interrupted'

/** SSE 看门狗：单次 reader.read() 超过此时长无数据视为断流（转续写路径） */
export const STREAM_READ_TIMEOUT_MS = 120_000

export class ApiClient {
  private readonly fetchImpl: typeof fetch
  private readonly sleep: (ms: number) => Promise<void>
  private readonly keyProvider: () => string | null

  constructor(deps: ApiClientDeps = {}) {
    this.fetchImpl = deps.fetchImpl ?? fetch.bind(globalThis)
    this.sleep = deps.sleep ?? defaultSleep
    this.keyProvider = deps.keyProvider ?? (() => null)
  }

  /** 单次聊天调用（含限流退避重试）；业务校验重试在上层 runner */
  async chat(opts: ChatOptions): Promise<ChatResult> {
    const key = this.keyProvider()
    if (!key) {
      throw new GmError('NO_API_KEY', '制作人，线路还没接——去「电脑 → 系统设置」填 Moonshot API Key，员工才能开工。')
    }

    const body: Record<string, unknown> = {
      model: opts.choice.model,
      messages: opts.messages,
      max_tokens: MAX_TOKENS,
      temperature: 1,
    }
    // k3 是推理模型：effort 必须显式传（不传 = 死屏 280s）；k2.6 非推理模型不带
    if (opts.choice.reasoningEffort) {
      body.reasoning_effort = opts.choice.reasoningEffort
    }
    if (opts.responseFormat) {
      body.response_format = opts.responseFormat
    }
    if (opts.stream) {
      body.stream = true
      body.stream_options = { include_usage: true }
    }

    let lastErr: GmError | null = null
    for (let attempt = 0; attempt <= RETRY_DELAYS_MS.length; attempt++) {
      if (attempt > 0) await this.sleep(RETRY_DELAYS_MS[attempt - 1])
      try {
        return await this.once(key, body, opts)
      } catch (err) {
        if (err instanceof GmError) {
          // 401/402 不退避；429 与 5xx 退避
          if (err.code === 'KEY_INVALID' || err.code === 'NO_API_KEY') throw err
          lastErr = err
          const status = (err.detail as { status?: number } | undefined)?.status
          if (status == null) {
            // 无 HTTP 状态 = 真断连（fetch 层异常），走指数退避
            if (err.code === 'NETWORK') continue
            throw err
          }
          // 有 HTTP 状态：429 / 5xx 才退避；4xx 是确定性错误，直接抛给玩家
          if (status === 429 || status >= 500) continue
          throw err
        }
        throw err
      }
    }
    throw (
      lastErr ??
      new GmError('QUOTA', '呼叫太频繁，员工们先去泡了杯茶。稍后再试或充值。')
    )
  }

  private async once(key: string, body: Record<string, unknown>, opts: ChatOptions): Promise<ChatResult> {
    let res: Response
    try {
      res = await this.fetchImpl(API_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${key}`,
        },
        body: JSON.stringify(body),
      })
    } catch (err) {
      throw new GmError('NETWORK', '信号丢了……检查网络后点「继续」，进度都在。', err)
    }

    if (!res.ok) {
      const translated = translateHttpError(res.status)
      throw new GmError(translated.code, translated.message, { status: res.status })
    }

    return opts.stream ? this.readStream(res, opts) : this.readJson(res)
  }

  private async readJson(res: Response): Promise<ChatResult> {
    let data: {
      choices?: { message?: { content?: string; reasoning_content?: string }; finish_reason?: string }[]
      usage?: { prompt_tokens?: number; completion_tokens?: number; total_tokens?: number; completion_tokens_details?: { reasoning_tokens?: number } }
    }
    try {
      data = (await res.json()) as typeof data
    } catch (err) {
      // 200 但非 JSON（代理错误页 / 响应截断）：包装成玩家可读错误
      throw new GmError(
        'NETWORK',
        '线路回了张看不懂的纸条（响应不是 JSON）——可能是网关或代理出错，稍后再试。',
        err,
      )
    }
    const choice = data.choices?.[0]
    return {
      content: choice?.message?.content ?? '',
      reasoning: choice?.message?.reasoning_content ?? '',
      finishReason: choice?.finish_reason ?? null,
      usage: normalizeUsage(data.usage),
    }
  }

  /** SSE 解析：正文与推理双通道；usage 在末尾 chunk（stream_options.include_usage） */
  private async readStream(res: Response, opts: ChatOptions): Promise<ChatResult> {
    if (!res.body) {
      throw new GmError('NETWORK', '信号丢了……流式通道没建立起来，再点一次试试。')
    }
    const reader = res.body.getReader()
    const decoder = new TextDecoder()
    let buffer = ''
    let content = ''
    let reasoning = ''
    let finishReason: string | null = null
    let usage: Usage = { prompt_tokens: 0, completion_tokens: 0, total_tokens: 0 }

    try {
      for (;;) {
        // 看门狗：单读超时（120s 无数据）视为断流，走部分产物续写路径
        let timeout: ReturnType<typeof setTimeout> | undefined
        const readResult = await Promise.race([
          reader.read(),
          new Promise<never>((_, reject) => {
            timeout = setTimeout(
              () => reject(new Error(`SSE 断流：${STREAM_READ_TIMEOUT_MS / 1000}s 无数据`)),
              STREAM_READ_TIMEOUT_MS,
            )
          }),
        ]).finally(() => clearTimeout(timeout))
        const { done, value } = readResult
        if (done) break
        buffer += decoder.decode(value, { stream: true })
        // SSE 事件以空行分隔
        let sep: number
        while ((sep = buffer.indexOf('\n\n')) >= 0) {
          const rawEvent = buffer.slice(0, sep)
          buffer = buffer.slice(sep + 2)
          for (const line of rawEvent.split('\n')) {
            if (!line.startsWith('data:')) continue
            const payload = line.slice(5).trim()
            if (payload === '[DONE]') continue
            let chunk: {
              choices?: {
                delta?: { content?: string; reasoning_content?: string }
                finish_reason?: string | null
              }[]
              usage?: {
                prompt_tokens?: number
                completion_tokens?: number
                total_tokens?: number
                completion_tokens_details?: { reasoning_tokens?: number }
              }
            }
            try {
              chunk = JSON.parse(payload)
            } catch {
              continue // 半包/心跳，跳过
            }
            const delta = chunk.choices?.[0]?.delta
            if (delta?.reasoning_content) {
              reasoning += delta.reasoning_content
              opts.onToken?.('reasoning', delta.reasoning_content, reasoning.length)
            }
            if (delta?.content) {
              content += delta.content
              opts.onToken?.('content', delta.content, content.length)
            }
            const fr = chunk.choices?.[0]?.finish_reason
            if (fr) finishReason = fr
            if (chunk.usage) usage = normalizeUsage(chunk.usage)
          }
        }
      }
    } catch (err) {
      if (content.length > 0) {
        // 已收到部分正文：不抛错（避免已收内容全丢、双倍烧 token），
        // 带哨兵 finishReason 返回，交给上层续写协议（与 'length' 同路径）
        return { content, reasoning, finishReason: STREAM_INTERRUPTED, usage }
      }
      throw new GmError('NETWORK', '员工写到一半睡着了（流中断）——自动续写或重派一次。', err)
    } finally {
      try {
        reader.releaseLock()
      } catch {
        // 忽略
      }
    }

    return { content, reasoning, finishReason, usage }
  }
}

function normalizeUsage(u?: {
  prompt_tokens?: number
  completion_tokens?: number
  total_tokens?: number
  completion_tokens_details?: { reasoning_tokens?: number }
}): Usage {
  const prompt = u?.prompt_tokens ?? 0
  const completion = u?.completion_tokens ?? 0
  return {
    prompt_tokens: prompt,
    completion_tokens: completion,
    total_tokens: u?.total_tokens ?? prompt + completion,
    reasoning_tokens: u?.completion_tokens_details?.reasoning_tokens,
  }
}
